import { X } from "@phosphor-icons/react";

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="bg-white/80 backdrop-blur border-b border-slate-200 px-8 py-6 flex items-end justify-between sticky top-0 z-20">
      <div>
        <h1 className="font-heading font-extrabold text-3xl lg:text-4xl leading-none text-[#16205A]">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-2">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

const STATUS = {
  paid: { bg: "#DCFCE7", text: "#15803D" },
  partial: { bg: "#FEF3C7", text: "#B45309" },
  pending: { bg: "#E0E7FF", text: "#3730A3" },
  overdue: { bg: "#FEE2E2", text: "#B91C1C" },
  todo: { bg: "#E2E8F0", text: "#334155" },
  in_progress: { bg: "#FEF3C7", text: "#B45309" },
  done: { bg: "#DCFCE7", text: "#15803D" },
  high: { bg: "#FEE2E2", text: "#B91C1C" },
  medium: { bg: "#FEF3C7", text: "#B45309" },
  low: { bg: "#E2E8F0", text: "#334155" },
  follow_up: { bg: "#E0E7FF", text: "#3730A3" },
  payment: { bg: "#FEE2E2", text: "#B91C1C" },
  email: { bg: "#DBEAFE", text: "#1D4ED8" },
  general: { bg: "#E2E8F0", text: "#334155" },
};

export function StatusBadge({ status, label, testid }) {
  const s = STATUS[status] || { bg: "#E2E8F0", text: "#334155" };
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center font-mono text-[10px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wide"
      style={{ backgroundColor: s.bg, color: s.text }}
    >
      {label || String(status).replace("_", " ")}
    </span>
  );
}

export function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0B1230]/60 backdrop-blur-sm fade-up" onClick={onClose}>
      <div className="card-soft w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="font-heading font-extrabold text-xl text-[#16205A]">{title}</h3>
          <button data-testid="modal-close" onClick={onClose} className="text-slate-400 hover:text-[#F49B1C] transition-colors">
            <X size={22} weight="bold" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#F49B1C]/60 focus:border-[#F49B1C] transition-shadow";

export function Field({ label, ...props }) {
  return (
    <div>
      <label className="font-semibold text-xs uppercase tracking-wide text-slate-500 block mb-1.5">{label}</label>
      <input {...props} className={inputCls} />
    </div>
  );
}

export function TextArea({ label, ...props }) {
  return (
    <div>
      <label className="font-semibold text-xs uppercase tracking-wide text-slate-500 block mb-1.5">{label}</label>
      <textarea {...props} className={`${inputCls} min-h-[90px]`} />
    </div>
  );
}

export function Select({ label, children, ...props }) {
  return (
    <div>
      <label className="font-semibold text-xs uppercase tracking-wide text-slate-500 block mb-1.5">{label}</label>
      <select {...props} className={inputCls}>
        {children}
      </select>
    </div>
  );
}

export function Button({ children, variant = "primary", ...props }) {
  const base =
    "inline-flex items-center justify-center gap-1.5 font-semibold text-sm px-4 py-2.5 rounded-lg transition-all active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none";
  const styles = {
    primary: "bg-[#F49B1C] hover:bg-[#E0890F] text-white shadow-sm shadow-orange-500/20",
    navy: "bg-[#16205A] hover:bg-[#1E2A6E] text-white shadow-sm",
    ghost: "bg-white border border-slate-300 hover:border-slate-400 text-slate-700",
    danger: "bg-white border border-red-200 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600",
  };
  return (
    <button {...props} className={`${base} ${styles[variant]}`}>
      {children}
    </button>
  );
}
